import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { TableProps } from 'antd'
import { Flex, Table, message } from 'antd'
import { SeedPedigree } from 'modules/domain/seedTracker/types'
import { SeedOrderStatus } from 'modules/domain/seedOrders/types'
import { listSeedOrders } from 'modules/domain/seedOrders/managers'
import { getSeedTrackersList } from 'modules/domain/seedTracker/managers'
import { useHistory } from 'react-router-dom'
import { generatePath } from 'react-router-dom'
import SeedOrdersRoutes from '../../../SeedOrders/route'
import SeedTrackerRoutes from '../../routes'
import { seedTrackerVarietyLabel } from '../../../SeedOrders/SeedOrderAdd'
import { mapSeedOrderApiRecordToSeedOrder, SeedOrder } from '../../../SeedOrders/SeedOrdersList/SeedOrdersList'
import { StatusLabel, rowCursorPointerStyle } from '../../../SeedOrders/styles'
import { PedigreeTag } from '../PedigreeTag'

const DEFAULT_PAGE_SIZE = 50

type SeedInventoryOrdersListProps = {
  seedInventoryId?: string
  productCardId?: string
  originCompanyId?: string
  seededPedigree?: SeedPedigree
  search?: string
}

const getStatusBackgroundColor = (status: SeedOrderStatus): string => {
  switch (status) {
    case SeedOrderStatus.Pending:
      return '#037BFF'
    case SeedOrderStatus.Approved:
      return '#2E7D32'
    case SeedOrderStatus.InTransit:
      return '#F57C00'
    case SeedOrderStatus.Completed:
      return '#20B02E'
    case SeedOrderStatus.Cancelled:
      return '#776D6D'
    default:
      return '#776D6D'
  }
}

const SeedInventoryOrdersList: React.FC<SeedInventoryOrdersListProps> = ({
  seedInventoryId,
  productCardId,
  originCompanyId,
  seededPedigree,
  search,
}) => {
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [filteredData, setFilteredData] = useState<SeedOrder[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const prevSearchRef = useRef(search)

  const load = useCallback(async () => {
    if (!productCardId || !originCompanyId || !seededPedigree) {
      setFilteredData([])
      return
    }

    setLoading(true)
    try {
      const [ordersRes, trackersRes] = await Promise.all([
        listSeedOrders({
          search: search?.trim() || undefined,
          product_card_id: productCardId,
          page: 1,
          page_size: 5000,
        }),
        getSeedTrackersList({}, {}, 1, 5000),
      ])

      const varietyBy = Object.fromEntries((trackersRes.data ?? []).map((r) => [r.id, seedTrackerVarietyLabel(r)]))

      const scopedOrders = (ordersRes.data ?? []).filter(
        (row) => row.origin_company_id === originCompanyId && row.current_pedigree === seededPedigree,
      )

      setFilteredData(scopedOrders.map((row) => mapSeedOrderApiRecordToSeedOrder(row, varietyBy)))
    } catch (e) {
      console.error(e)
      message.error('Could not load seed orders.')
      setFilteredData([])
    } finally {
      setLoading(false)
    }
  }, [originCompanyId, productCardId, search, seededPedigree])

  useEffect(() => {
    if (prevSearchRef.current !== search) {
      prevSearchRef.current = search
      setPage(1)
    }
  }, [search])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    setPage(1)
  }, [originCompanyId, seededPedigree, productCardId])

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize
    return filteredData.slice(start, start + pageSize)
  }, [filteredData, page, pageSize])

  const goToDetails = (record: SeedOrder) => {
    const path = generatePath(SeedOrdersRoutes.SeedOrderDetails, { id: record.id })
    history.push(path, {
      seedOrder: record,
      returnTo: seedInventoryId
        ? { route: SeedTrackerRoutes.SeedInventoryList, params: { id: seedInventoryId } }
        : undefined,
    })
  }

  const columns: TableProps<SeedOrder>['columns'] = [
    {
      title: 'SO / PO / SIV #',
      dataIndex: 'soPoSivNumber',
      key: 'soPoSivNumber',
    },
    {
      title: 'Variety',
      dataIndex: 'variety',
      key: 'variety',
    },
    {
      title: 'Pedigree',
      dataIndex: 'pedigree',
      key: 'pedigree',
      render: (pedigree: SeedPedigree) => <PedigreeTag pedigree={pedigree} />,
    },
    {
      title: 'Bushels',
      dataIndex: 'bushels',
      key: 'bushels',
    },
    {
      title: 'Origin Grower',
      dataIndex: 'originGrower',
      key: 'originGrower',
    },
    {
      title: 'Destination Grower',
      dataIndex: 'destinationGrower',
      key: 'destinationGrower',
    },
    {
      title: 'Last Updated Date',
      dataIndex: 'lastUpdatedDate',
      key: 'lastUpdatedDate',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: SeedOrderStatus) => (
        <StatusLabel backgroundColor={getStatusBackgroundColor(status)}>{status}</StatusLabel>
      ),
    },
  ]

  const rowKey: TableProps<SeedOrder>['rowKey'] = (record) => record.id

  return (
    <Flex vertical gap="middle">
      <Table<SeedOrder>
        columns={columns}
        dataSource={pagedData}
        rowKey={rowKey}
        loading={loading}
        onRow={(record) => ({
          onClick: () => goToDetails(record),
          style: rowCursorPointerStyle,
        })}
        pagination={{
          current: page,
          pageSize,
          total: filteredData.length,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          position: ['bottomLeft'],
          onChange: (nextPage, nextPageSize) => {
            if (nextPageSize != null && nextPageSize !== pageSize) {
              setPageSize(nextPageSize)
              setPage(1)
            } else {
              setPage(nextPage)
            }
          },
        }}
      />
    </Flex>
  )
}

export default SeedInventoryOrdersList
